import { QueryClient, useQuery } from '@tanstack/react-query';
import { getSessionReport, probeSessionReport, probeSessionReportLessons } from './api';
import { sessionReportKeys } from './keys';

const MINUTE = 1000 * 60;

/**
 * 탭 노출 여부. 답(available·unavailable)은 한 세션 동안 캐시한다(배포가 바뀌면 새로고침으로 다시 묻는다).
 * 네트워크 오류·5xx 는 오류 상태로 남아 캐시된 답이 아니다 — 정산 화면에 다시 들어오면 다시 묻는다.
 */
export const sessionReportAvailabilityQuery = (centerId: string) => ({
    queryKey: sessionReportKeys.availability(centerId),
    queryFn: () => probeSessionReport(centerId),
    staleTime: Infinity,
    cacheTime: Infinity,
});

export const useSessionReportAvailability = ({ centerId }: { centerId?: string }) =>
    useQuery({
        ...sessionReportAvailabilityQuery(centerId ?? ''),
        // 전역 기본값이 enabled:false 라 반드시 명시한다.
        enabled: !!centerId,
        retry: false,
        // 확정 답(404·403·400)은 다시 묻지 않는다. 일시 실패(5xx·네트워크)일 때만 창으로 돌아오면 다시 묻는다.
        refetchOnWindowFocus: (query) => (query.state.status === 'error' ? 'always' : false),
    });

/**
 * 월 세션 현황 보고서. 무거운 요청이라 5분 캐시·포커스 재조회 없음·자동 재시도 없음.
 * 유령 기준 일수를 바꾸는 동안에는 이전 결과를 그대로 보여 준다(keepPreviousData).
 */
export const SESSION_REPORT_STALE_TIME = 5 * MINUTE;

/** 화면 조회와 엑셀 만들기가 같은 캐시를 쓰도록 쿼리 정의를 한곳에서 만든다. */
export const sessionReportQuery = ({
    centerId,
    yearMonth,
    ghostDays,
    coachId,
}: {
    centerId: string;
    yearMonth: string;
    ghostDays: number;
    coachId?: string;
}) => ({
    queryKey: sessionReportKeys.report(centerId, yearMonth, ghostDays, coachId ?? ''),
    queryFn: () => getSessionReport({ centerId, yearMonth, ghostDays, coachId }),
    staleTime: SESSION_REPORT_STALE_TIME,
});

/**
 * 캐시를 건너뛰고 실제로 다시 받는다(엑셀 만들기용). 받은 값은 같은 키 캐시에도 들어가 화면과 맞춰진다.
 * 시간 초과는 오류로 던지므로 캐시에 성공으로 남지 않는다.
 */
export const fetchSessionReportFresh = (
    queryClient: QueryClient,
    params: { centerId: string; yearMonth: string; ghostDays: number; coachId?: string },
) => queryClient.fetchQuery({ ...sessionReportQuery(params), staleTime: 0 });

export const useSessionReport = ({
    centerId,
    yearMonth,
    ghostDays,
    coachId,
    enabled = true,
    keepPreviousData = true,
}: {
    centerId?: string;
    yearMonth: string;
    ghostDays: number;
    coachId?: string;
    enabled?: boolean;
    keepPreviousData?: boolean;
}) =>
    useQuery({
        ...sessionReportQuery({ centerId: centerId ?? '', yearMonth, ghostDays, coachId }),
        enabled: enabled && !!centerId && !!yearMonth,
        refetchOnWindowFocus: false,
        retry: false,
        keepPreviousData,
    });

/**
 * 「수업 보기」 버튼을 보일지. 서버에 기능이 없으면(구서버 404) 버튼을 아예 숨긴다.
 * 확정 답은 한 세션 동안 캐시하고, 일시 실패면 버튼을 숨긴 채 다음에 다시 묻는다.
 * 세션 현황 전체를 다시 받을 때(sessionReportKeys.all 무효화) 함께 다시 묻지 않게 별도 키 접두는 같지만 staleTime 무한.
 */
export const useSessionReportLessonsAvailability = (centerId?: string) =>
    useQuery({
        queryKey: sessionReportKeys.lessonsAvailability(centerId ?? ''),
        queryFn: () => probeSessionReportLessons(centerId ?? ''),
        enabled: !!centerId,
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false,
    });
